import { useMemo } from "react";
import { Box, Heading, Tooltip, Icon, HStack } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { useSelector } from "react-redux";
import _ from "lodash";
import { Helmet } from "react-helmet";

import FilterForm from "../../components/FilterForm";
import AllTickets from "../../components/organization/reports/AllTickets";
import { getReportDescription, getReportRangeString } from "../../store/helpers";
import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import { TICKET_SUMMARY_COLUMN_DEFINITIONS } from "../../store/renderConfig";
import { useReportPresentation } from "../../hooks/useReportPresentation";
import { downloadTicketSummaryCsvFiles } from "../../store/ticketSummaryExport";

const TicketsPage = ({ showFilterForm = true, showTitle = 1 }) => {
  const { reportRange, reportsData, organizationId } = useSelector(
    (state) => state.defaultReducer
  );
  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_organization_tickets
      : REPORT_TYPE_IDS.monthly_organization_tickets;
  
  const report = reportsData.find(
    (a) =>
      a.reportTypeId === reportTypeId && a.organizationId === organizationId
  );
  const data = report?.data || [];
  const reportTitle = `Organization Tickets`;
  const {
    effectiveRenderConfig,
    isSaving,
    presentationSummaryItems,
    saveRenderConfig,
    setRenderConfig,
  } = useReportPresentation({
    reportTypeId,
    columnDefinitions: TICKET_SUMMARY_COLUMN_DEFINITIONS,
  });
  const csvScopes = useMemo(
    () =>
      data[0]
        ? [{
            ...data[0],
            scopeName: data[0].name || "Entire Organization",
          }]
        : [],
    [data]
  );

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>{getReportRangeString(reportRange)} {reportTitle}</title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : null}
      {!_.isEmpty(data) ? (
        <HStack flexDirection={"row"} justifyContent={"space-between"} >
          <Box>
            <Heading fontSize="34px" color="#222" fontWeight="700">
              {reportTitle}
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    {getReportDescription(reportRange).Tickets}
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            </Heading>
          </Box>
          <DownloadReport
            reportRange={reportRange}
            reportTypeId={reportTypeId}
            renderConfig={effectiveRenderConfig}
            onSave={saveRenderConfig}
            isSaving={isSaving}
            onDownloadCsv={() =>
              downloadTicketSummaryCsvFiles({
                scopes: csvScopes,
                renderConfig: effectiveRenderConfig,
                filenamePrefix: "organization_tickets",
              })
            }
          />
        </HStack>
      ) : null}
      {!_.isEmpty(data) ? (
        <>
          <ReportPresentationControls
            renderConfig={effectiveRenderConfig}
            onChange={setRenderConfig}
            columnDefinitions={TICKET_SUMMARY_COLUMN_DEFINITIONS}
            defaultSortColumn="total"
          />
          <ReportPresentationSummary items={presentationSummaryItems} />
          <AllTickets
            data={data[0]}
            renderConfig={effectiveRenderConfig}
            onRenderConfigChange={setRenderConfig}
          />
        </>
      ) : (
        <SelectBranchInfo message={'Update Filters and Hit Search to retrieve data'}/>
      )}
    </Box>
  );
};

export default TicketsPage;
